<template>
  <b-modal
    ref="roleModal"
    size="xl"
    centered
    modal-class="configuration-modal-tabs role-modal"
    static
    hide-footer
    @hidden="hide"
  >
    <div
      slot="modal-title"
      class="d-flex align-items-center"
    >
      {{ actionTitle }} {{ $tc('configuration.sections.roles', 1).toLowerCase() }}
      <b-badge
        v-if="internalRole.isSystemRole"
        class="ml-3 px-2 system-badge"
      >
        {{ $t('configuration.modal.systemBadge') }}
      </b-badge>
    </div>
    <b-row class="px-0">
      <b-col class="px-2 pt-1 pb-2">
        <div>
          <b-tabs
            v-model="activeTabIndex"
            pills
            card
            vertical
            class="vt-tabs--pills"
          >
            <b-tab active>
              <template slot="title">
                <i
                  class="far fa-list-ul mr-2"
                  aria-hidden="true"
                />
                {{ $t('configuration.modal.basicData') }}
              </template>
              <b-row class="p-3 scroll-area">
                <b-col>
                  <b-row class="mb-3">
                    <b-col cols="12">
                      <span class="title">{{ $t('configuration.modal.basicData') }}</span>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col cols="6">
                      <div class="form-label">
                        <label>{{ $t('configuration.modal.role.roleName') }}</label>
                      </div>
                      <vt-input
                        ref="name"
                        v-model="internalRole.name"
                        class="form-control-sm"
                      />
                    </b-col>
                    <div class="w-100 my-2" />
                    <b-col cols="6">
                      <div class="form-label">
                        <label>{{ $t('configuration.modal.role.roleCode') }}</label>
                      </div>
                      <vt-input
                        ref="code"
                        v-model="internalRole.code"
                        class="form-control-sm"
                      />
                    </b-col>
                    <div class="w-100 my-2" />
                    <b-col cols="12">
                      <div class="form-label">
                        <label>{{ $t('configuration.modal.description') }}</label>
                      </div>
                      <b-form-textarea
                        v-model="internalRole.description"
                        rows="3"
                        no-resize
                      />
                    </b-col>
                    <div class="w-100 my-2" />
                    <b-col
                      cols="3"
                    >
                      <div class="switch-btn p-0 d-flex align-items-center">
                        <vt-combo-select
                          id="userTypeSelect"
                          v-model="internalRole.userType"
                          :searchable="false"
                          :allow-empty="false"
                          track-by="value"
                          :custom-label="localizedUserType"
                          :options="userTypeOptions"
                        />
                      </div>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
              <div class="footer-area component-bg d-flex end px-3 py-2 align-items-center justify-content-end">
                <div>
                  <b-button
                    :disabled="!internalRole.name || !internalRole.description"
                    size="sm"
                    class="btn--primary"
                    @click="update"
                  >
                    <i
                      class="fal fa-check mr-2"
                      aria-hidden="true"
                    />
                    {{ actionTitle }}
                  </b-button>
                </div>
              </div>
            </b-tab>
            <b-tab :disabled="!internalRole.id">
              <template slot="title">
                <i
                  class="far fa-user-lock mr-2"
                  aria-hidden="true"
                />
                {{ $tc('configuration.modal.operation', 2) }}
              </template>
              <b-row
                style="height: 100%"
                class="p-3"
              >
                <b-col cols="12">
                  <span class="title">{{ $tc('configuration.modal.operation', 2) }}</span>
                </b-col>
                <b-col
                  id="operationsList"
                  class="mt-2 tab-scroll-content without-footer association-table pl-0 pr-2 mx-2"
                >
                  <div class="d-flex mb-1 header-row">
                    <div class="d-flex justify-content-between w-100">
                      <div class="fixed-column">
                        <vt-input
                          v-model="filter"
                          :name="$t('configuration.modal.filterOperations')"
                          class="form-control-sm"
                          :placeholder="$t('configuration.modal.filterOperations')"
                        />
                      </div>
                      <div class="check-column group d-flex align-items-center">
                        <operation-group-filter
                          :target-id="'roleModal'"
                          @change="groupFilter = $event"
                        />
                      </div>
                      <div class="check-column d-flex align-items-center">
                        <span class="pl-2">{{ $t('configuration.modal.associate') }}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    v-for="(operation, index) in filteredInternalOperations"
                    :key="index"
                    class="d-flex"
                  >
                    <div class="d-flex justify-content-between bottom-line w-100">
                      <div class="fixed-column">
                        <span>{{ operation.translatedName }}</span>
                      </div>
                      <div class="check-column group d-flex align-items-center">
                        <span>
                          <div class="d-flex">
                            <div class="d-flex align-items-center justify-content-center">
                              <div class="group-icon">
                                <i
                                  v-if="operation.type === OPERATION_GROUP.ACCESS.value"
                                  :class="OPERATION_GROUP.ACCESS.icon"
                                  aria-hidden="true"
                                />
                                <i
                                  v-else
                                  :class="getOperationGroup(operation).icon"
                                  aria-hidden="true"
                                />
                              </div>
                              <span v-if="operation.type === OPERATION_GROUP.ACCESS.value">{{ OPERATION_GROUP.ACCESS.translatedName }}</span>
                              <span v-else>{{ getOperationGroup(operation).translatedName }}</span>
                            </div>
                          </div>
                        </span>
                      </div>
                      <div class="check-column d-flex align-items-center">
                        <b-form-checkbox
                          v-model="operation.assigned"
                          class="ml-2"
                          @change="operationAssignedChanged(operation)"
                        />
                      </div>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-tab>
          </b-tabs>
        </div>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { cloneDeep, isEmpty, differenceWith, orderBy } from 'lodash'
import OperationConfigurationServices from '@/api/resources/configuration/peopleManagement/operation/OperationConfigurationServices'
import RoleOperationConfigurationServices from '@/api/resources/configuration/peopleManagement/role/RoleOperationConfigurationServices'
import OperationGroupFilter from '@/modules/configurationModule/detail/peopleManagement/OperationGroupFilter'
import { OPERATION_GROUP } from '@/api/resources/configuration/peopleManagement/OperationGroups'
import { ASSOCIATION_TYPE } from '@/api/resources/configuration/peopleManagement/AssociationTypes'
import { UserType, UserTypeTranslationKeys } from '@/api/resources/common/initialization/common-initialization.dto.ts'
import { enumKeys } from '@/utils/enums.ts'
import { GridUtils } from '@/shared/util/grid-utils'

const DATA_TAB = 0
const OPERATIONS_TAB = 1

export default {
    name: 'RoleModal',
    components: { OperationGroupFilter },
    props: {
        actionTitle: String
    },
    data() {
        return {
            internalRole: {},
            internalOperations: [],
            internalOriginalOperations: [],
            filter: '',
            operationsList: null,
            activeTabIndex: DATA_TAB,
            groupFilter: null
        }
    },
    computed: {
        userTypeOptions() {
            return enumKeys(UserType)
        },
        filteredInternalOperations() {
            return this.internalOriginalOperations.filter(operation => {
                if (this.groupFilter) {
                    if (this.groupFilter.value === this.OPERATION_GROUP.ACCESS.value) {
                        return operation.translatedName.toLowerCase().includes(this.filter.toLowerCase()) &&
                            operation.type === this.OPERATION_GROUP.ACCESS.value
                    } else {
                        return operation.translatedName.toLowerCase().includes(this.filter.toLowerCase()) &&
                            this.getOperationGroup(operation).value === this.groupFilter.value
                    }
                } else {
                    return operation.translatedName.toLowerCase().includes(this.filter.toLowerCase())
                }
            })
        },
        OPERATION_GROUP() {
            return OPERATION_GROUP
        },
        ASSOCIATION_TYPE() {
            return ASSOCIATION_TYPE
        }
    },
    watch: {
        groupFilter() {
            this.$root.$emit('bv::hide::popover', 'operationModal-group-pop')
        }
    },
    methods: {
        localizedUserType(userType) {
            const translation = UserTypeTranslationKeys.find(userTypeTranslation => userTypeTranslation.code === userType)
            return this.$tc(translation.translationKey)
        },
        async show(selectedRole, associationType) {
            let targetTab = DATA_TAB
            this.filter = ''
            if (selectedRole && !isEmpty(selectedRole)) {
                await this.setRoleOperations(selectedRole)
            } else {
                this.clearRoleData()
            }
            if (associationType) {
                if (associationType === this.ASSOCIATION_TYPE.OPERATION.key) {
                    targetTab = OPERATIONS_TAB
                }
            }
            this.activeTabIndex = targetTab
            this.$refs.roleModal.show()
            this.$nextTick(() => {
                this.$refs.name.focus()
            })
        },
        getOperationGroup(associationOperation) {
            let operationGroup = ''
            let regExp = ''
            for (let operation in this.OPERATION_GROUP) {
                if (this.OPERATION_GROUP[operation].groups) {
                    this.OPERATION_GROUP[operation].groups.forEach(group => {
                        regExp = new RegExp('^' + associationOperation.group)
                        if (regExp.test(group)) operationGroup = this.OPERATION_GROUP[operation]
                    })
                }
            }
            return operationGroup
        },
        async createRoleOperation(operation) {
            const roleOperationCreateRequest = {
                operationId: operation.id,
                roleId: this.internalRole.id
            }
            const [err, response] = await this.toCall(RoleOperationConfigurationServices.createRoleOperation(roleOperationCreateRequest))
            if (err) {
                operation.assigned = !operation.assigned
                this.showErrorModalGlobal(err.message)
                return
            }
            this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.roleOperationCreated', 1))
            operation.roleOperationId = response.id
        },
        async deleteRoleOperation(operation) {
            let roleOperationId = operation.roleOperationId
            const [err] = await this.toCall(RoleOperationConfigurationServices.deleteRoleOperation(roleOperationId))
            if (err) {
                operation.assigned = !operation.assigned
                this.showErrorModalGlobal(err.message)
            }
            this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.roleOperationDeleted', 1))
        },
        async operationAssignedChanged(operation) {
            if (operation.assigned) {
                await this.createRoleOperation(operation)
            } else await this.deleteRoleOperation(operation)
        },
        async setRoleOperations(createdRole) {
            const filterModel = {
                userType: GridUtils.getSetFilterModel([createdRole.userType.enumValue])
            }
            this.internalRole = {
                id: createdRole.id,
                description: createdRole.description,
                userType: createdRole.userType.enumValue,
                name: createdRole.name,
                code: createdRole.code
            }

            cloneDeep(createdRole)
            this.updateLoading(true)
            const [err, response] = await this.toCall(Promise.all([
                OperationConfigurationServices.paginator(filterModel, { size: 1000 }),
                RoleOperationConfigurationServices.getRoleOperationsByRoleId(this.internalRole.id)]))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            const operations = response[0].content
            const roleOperations = response[1]
            roleOperations.forEach(roleOperation => {
                let assignedOperation = operations.find(operation => operation.id === roleOperation.operation.id)
                if (assignedOperation) {
                    assignedOperation.assigned = true
                    assignedOperation.roleOperationId = roleOperation.id
                }
            })
            this.internalOperations = orderBy(differenceWith(operations, roleOperations), ['assigned'], ['asc'])
            this.internalOriginalOperations = cloneDeep(this.internalOperations)
        },
        hide() {
            this.clearRoleOperations()
            this.$refs.roleModal.hide()
        },
        update() {
            if (this.internalRole.id) this.$emit('update', this.internalRole)
            else this.$emit('create', this.internalRole)
        },
        clearRoleOperations() {
            this.internalOperations = []
        },
        clearRoleData() {
            this.internalRole = {
                id: null,
                description: '',
                userType: UserType.PERSONNEL,
                name: '',
                code: ''
            }
        }
    }
}
</script>

<style lang="scss">
    input[type="checkbox"].disabled:hover {
        cursor: not-allowed;
    }
    .role-modal .modal-body {
        border-radius: .3rem;
    }
    .role-modal .tab-content {
        height: calc(100vh - 400px);
        overflow-y: auto;
    }
    @media only screen and (max-width: 1366px) and (min-width: 1360px) {
        .role-modal .tab-content {
            height: calc(100vh - 160px);
        }
    }
</style>
